import { Check } from 'lucide-react';
import { Controller, useWatch } from 'react-hook-form';

import { Combobox } from '@nesso/ui/primitives/combobox';
import { Field, FieldLabel, FieldSlot } from '@nesso/ui/primitives/field';
import { InputField } from '@nesso/ui/primitives/input-field';
import { RadioGroup } from '@nesso/ui/primitives/radio-group';
import { RadioGroupItemField } from '@nesso/ui/primitives/radio-group-item-field';
import { ToggleGroup, ToggleGroupItem } from '@nesso/ui/primitives/toggle-group';

import { stripNonDigits } from '@/shared/lib/format-utils';
import { FieldReveal } from '@/shared/ui/forms/field-reveal';

import {
    LOCATION_NAME_HELPER_TEXT,
    PLACE_OF_SERVICE_OPTIONS,
    STATE_OPTIONS,
} from '../../config/save-location.config';
import type { useSaveLocationForm } from '../../model/use-save-location-form';

type Props = {
    form: ReturnType<typeof useSaveLocationForm>;
};

export function SaveLocationFields({ form: saveLocation }: Props) {
    const { form, setOffersTelehealth } = saveLocation;
    const { control, formState } = form;
    const offersTelehealth = useWatch({ control, name: 'offersTelehealth' });

    return (
        <div className="@container flex flex-col gap-6">
            <Controller
                control={control}
                name="name"
                render={({ field, fieldState }) => (
                    <InputField
                        {...field}
                        id="location-name"
                        label="Name"
                        description={LOCATION_NAME_HELPER_TEXT}
                        error={fieldState.error?.message ?? fieldState.invalid}
                    />
                )}
            />
            <Controller
                control={control}
                name="street1"
                render={({ field, fieldState }) => (
                    <InputField
                        {...field}
                        id="location-street1"
                        label="Street address"
                        error={fieldState.error?.message ?? fieldState.invalid}
                    />
                )}
            />
            <FieldReveal
                label="Suite, floor, office number"
                hasSavedValue={Boolean(formState.defaultValues?.street2)}
            >
                <Controller
                    control={control}
                    name="street2"
                    render={({ field, fieldState }) => (
                        <InputField
                            {...field}
                            value={field.value ?? ''}
                            id="location-street2"
                            label="Suite, floor, office number (optional)"
                            error={fieldState.error?.message ?? fieldState.invalid}
                        />
                    )}
                />
            </FieldReveal>
            <div className="flex flex-col gap-4 sm:@min-[300px]:grid sm:@min-[300px]:grid-cols-[minmax(0,1fr)_96px] sm:@min-[300px]:gap-x-2 sm:@min-[300px]:gap-y-6 sm:@min-[380px]:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_96px]">
                <div className="sm:@min-[300px]:col-span-2 sm:@min-[380px]:col-span-1">
                    <Controller
                        control={control}
                        name="city"
                        render={({ field, fieldState }) => (
                            <InputField
                                {...field}
                                id="location-city"
                                label="City"
                                error={fieldState.error?.message ?? fieldState.invalid}
                            />
                        )}
                    />
                </div>
                <Controller
                    control={control}
                    name="state"
                    render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                            <FieldLabel htmlFor="location-state">State</FieldLabel>
                            <Combobox
                                id="location-state"
                                options={STATE_OPTIONS}
                                value={field.value}
                                onValueChange={field.onChange}
                                placeholder="Select state"
                                emptyMessage="No state found"
                                aria-invalid={fieldState.invalid}
                            />
                            <FieldSlot errorMessage={fieldState.error?.message} />
                        </Field>
                    )}
                />
                <div className="w-24 shrink-0">
                    <Controller
                        control={control}
                        name="zip"
                        render={({ field, fieldState }) => (
                            <InputField
                                {...field}
                                id="location-zip"
                                label="ZIP"
                                maxLength={5}
                                inputMode="numeric"
                                onChange={(event) =>
                                    field.onChange(stripNonDigits(event.target.value))
                                }
                                error={fieldState.error?.message ?? fieldState.invalid}
                            />
                        )}
                    />
                </div>
            </div>
            <Controller
                control={control}
                name="placeOfServiceCode"
                render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                        <FieldLabel htmlFor="location-place-of-service">
                            Place of service code
                        </FieldLabel>
                        <Combobox
                            id="location-place-of-service"
                            options={PLACE_OF_SERVICE_OPTIONS}
                            value={field.value}
                            onValueChange={field.onChange}
                            placeholder="Select place of service"
                            emptyMessage="No place of service found"
                            aria-invalid={fieldState.invalid}
                        />
                        <FieldSlot errorMessage={fieldState.error?.message} />
                    </Field>
                )}
            />
            <Controller
                control={control}
                name="offersTelehealth"
                render={({ field, fieldState }) => (
                    <div className="flex flex-col gap-3">
                        <p id="location-telehealth-label" className="text-base text-foreground">
                            Do you provide telehealth services from this location?
                        </p>
                        <ToggleGroup
                            type="single"
                            aria-labelledby="location-telehealth-label"
                            aria-describedby={
                                fieldState.error ? 'location-telehealth-error' : undefined
                            }
                            aria-invalid={fieldState.invalid}
                            variant="outline"
                            shape="pill"
                            // `ToggleGroup`'s own defaults are `justify-center gap-1` — this
                            // pair sits left-aligned with a 16px gap instead.
                            className="justify-start gap-4"
                            value={
                                field.value === undefined ? undefined : field.value ? 'yes' : 'no'
                            }
                            onValueChange={(value) => {
                                if (!value) return;
                                setOffersTelehealth(value === 'yes');
                            }}
                        >
                            <ToggleGroupItem value="yes" className="w-24 gap-1.5">
                                {field.value === true && (
                                    <span className="flex size-4 items-center justify-center rounded-full bg-primary text-primary-foreground">
                                        <Check className="size-3" />
                                    </span>
                                )}
                                Yes
                            </ToggleGroupItem>
                            <ToggleGroupItem value="no" className="w-24">
                                No
                            </ToggleGroupItem>
                        </ToggleGroup>
                        {fieldState.error && (
                            <p id="location-telehealth-error" className="text-sm text-destructive">
                                {fieldState.error.message}
                            </p>
                        )}
                    </div>
                )}
            />
            {/* Shown only while the telehealth answer is Yes; switching to No both hides this
                and discards the stored choice via `setOffersTelehealth`. */}
            {offersTelehealth === true && (
                <Controller
                    control={control}
                    name="defaultAppointmentModality"
                    render={({ field, fieldState }) => (
                        <div className="flex flex-col gap-3 rounded-sm bg-card p-4">
                            <p id="location-modality-label" className="text-base text-foreground">
                                Which would you like to default to for new appointments?
                            </p>
                            <RadioGroup
                                aria-labelledby="location-modality-label"
                                aria-describedby={
                                    fieldState.error ? 'location-modality-error' : undefined
                                }
                                aria-invalid={fieldState.invalid}
                                value={field.value ?? undefined}
                                onValueChange={field.onChange}
                            >
                                <RadioGroupItemField value="IN_PERSON" label="In-person" />
                                <RadioGroupItemField
                                    value="TELEHEALTH"
                                    label="Telehealth"
                                    description="Address won't be shown to client"
                                />
                            </RadioGroup>
                            {fieldState.error && (
                                <p
                                    id="location-modality-error"
                                    className="text-sm text-destructive"
                                >
                                    {fieldState.error.message}
                                </p>
                            )}
                        </div>
                    )}
                />
            )}
            {formState.errors.root && (
                <p className="text-sm text-destructive">{formState.errors.root.message}</p>
            )}
        </div>
    );
}
